/**
 * Group means with error bars: reading the data, working out SD, SE and confidence
 * intervals, and choosing which one a chart draws.
 *
 * Three kinds of error bar are in common use and they answer different questions
 * (Cumming, Fidler & Vaux, 2007):
 *
 * - **SD**, the standard deviation, describes the spread of the data. It does not shrink
 *   as the sample grows.
 * - **SE**, the standard error of the mean, SD ÷ √n, describes how far the mean might be
 *   from the population mean.
 * - **CI**, the confidence interval, is the mean ± t × SE with t from the t distribution
 *   on n − 1 degrees of freedom. For n of about 10 or more the 95% interval is close to
 *   mean ± 2 × SE.
 *
 * Input is either summary statistics (`Group, mean, SD, n`) or raw values
 * (`Group: 4, 5, 6`).
 */

import { standardDeviation } from "@/lib/stats/descriptive";
import { studentTQuantile } from "@/lib/stats/distributions";
import { parseLabeledRows, parseRowGroups } from "@/lib/tool-kit/charts/parse";

export type ErrorMeasure = "sd" | "se" | "ci";

export interface BarGroup {
  label: string;
  n: number;
  mean: number;
  /** Sample standard deviation; NaN when it cannot be known (one raw value). */
  sd: number;
}

export type BarIssueKind = "badRow" | "badN" | "badSd" | "single";

export interface BarIssue {
  kind: BarIssueKind;
  /** The group's label, or the 1-based line for a row with none. */
  text: string;
}

export interface BarParse {
  groups: BarGroup[];
  issues: BarIssue[];
}

export const MAX_GROUPS = 24;

const WHOLE = 1e-9;

/** Read `Group, mean, SD, n` rows, optionally under a header. */
export function parseSummary(text: string): BarParse {
  const table = parseLabeledRows(text);
  const groups: BarGroup[] = [];
  const issues: BarIssue[] = [];
  for (const row of table.rows) {
    const [mean, sd, n] = row.values;
    const label = row.label || String(row.line);
    if (mean === null || sd === null || n === null || row.values.length < 3) {
      issues.push({ kind: "badRow", text: label });
    } else if (!(sd >= 0)) {
      issues.push({ kind: "badSd", text: label });
    } else if (!(n >= 1) || Math.abs(n - Math.round(n)) > WHOLE) {
      issues.push({ kind: "badN", text: label });
    } else {
      groups.push({ label: row.label || label, n: Math.round(n), mean, sd });
    }
  }
  return { groups: groups.slice(0, MAX_GROUPS), issues };
}

/** Read `Group: v1, v2, v3` lines and work out each group's mean and SD. */
export function parseRaw(text: string): BarParse {
  const parsed = parseRowGroups(text);
  const groups: BarGroup[] = [];
  const issues: BarIssue[] = [];
  for (const [i, values] of parsed.groups.entries()) {
    const label = parsed.names[i] ?? String(i + 1);
    const n = values.length;
    const mean = values.reduce((sum, value) => sum + value, 0) / n;
    if (n < 2) {
      issues.push({ kind: "single", text: label });
    }
    groups.push({
      label,
      n,
      mean,
      sd: n < 2 ? Number.NaN : standardDeviation(values),
    });
  }
  return { groups: groups.slice(0, MAX_GROUPS), issues };
}

export interface GroupStats {
  group: BarGroup;
  se: number;
  /** Half the width of the confidence interval. */
  ciHalf: number;
}

/** The t multiplier for a two-sided interval at `level` percent with n − 1 df. */
export function tMultiplier(level: number, n: number): number {
  if (n < 2) {
    return Number.NaN;
  }
  return studentTQuantile(1 - (1 - level / 100) / 2, n - 1);
}

export function groupStats(group: BarGroup, level: number): GroupStats {
  const se = group.n >= 2 ? group.sd / Math.sqrt(group.n) : Number.NaN;
  return { group, se, ciHalf: tMultiplier(level, group.n) * se };
}

/** The half-width an error bar of this kind has, or null when it cannot be drawn. */
export function errorHalf(
  stats: GroupStats,
  measure: ErrorMeasure
): number | null {
  let value = stats.ciHalf;
  if (measure === "sd") {
    value = stats.group.sd;
  } else if (measure === "se") {
    value = stats.se;
  }
  return Number.isFinite(value) ? value : null;
}

/** Groups for which the chosen bar cannot be drawn (SE and CI need n of 2 or more). */
export function groupsWithoutBar(
  stats: readonly GroupStats[],
  measure: ErrorMeasure
): string[] {
  return stats
    .filter((entry) => errorHalf(entry, measure) === null)
    .map((entry) => entry.group.label);
}

/** The value range the chart must cover: every mean ± its bar. */
export function valueRange(
  stats: readonly GroupStats[],
  measure: ErrorMeasure
): [number, number] {
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (const entry of stats) {
    const half = errorHalf(entry, measure) ?? 0;
    low = Math.min(low, entry.group.mean - half);
    high = Math.max(high, entry.group.mean + half);
  }
  return [low, high];
}

/** A confidence level typed by the reader, or null when it is outside 50–99.9. */
export function usableLevel(level: number | null): number | null {
  return level !== null && level >= 50 && level <= 99.9 ? level : null;
}
